import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

const ReCaptcha = forwardRef(
  (
    { sitekey, onChange, onExpired, onError, theme = "light", size = "normal", className = "" },
    ref,
  ) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const checkIntervalRef = useRef(null);

    const renderRecaptcha = () => {
      // Prevent double rendering
      if (widgetIdRef.current !== null) {
        return;
      }

      if (!sitekey) {
        console.error("ReCaptcha: siteKey prop is required");
        return;
      }

      if (!containerRef.current) {
        console.error("ReCaptcha: container ref not available");
        return;
      }

      if (!window.grecaptcha || !window.grecaptcha.render) {
        console.error("ReCaptcha: grecaptcha not ready");
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: sitekey,
          theme: theme,
          size: size,
          callback: (token) => {
            if (onChange) onChange(token);
          },
          "expired-callback": () => {
            if (onExpired) onExpired();
            if (onChange) onChange(null);
          },
          "error-callback": () => {
            const error = new Error("reCAPTCHA error");
            if (onError) onError(error);
            if (onChange) onChange(null);
          },
        });
        console.log("reCAPTCHA rendered successfully, widgetId:", widgetIdRef.current);
      } catch (error) {
        console.error("Error rendering reCAPTCHA:", error);
        if (onError) onError(error);
      }
    };

    useEffect(() => {
      // Check if already loaded (via flag or grecaptcha object)
      if (window.recaptchaLoaded || (window.grecaptcha && window.grecaptcha.render)) {
        renderRecaptcha();
        return;
      }

      // Define callback for when recaptcha loads
      const originalCallback = window.onRecaptchaLoad;
      window.onRecaptchaLoad = () => {
        // Call original callback if it exists
        if (originalCallback) originalCallback();
        // Then render our captcha
        renderRecaptcha();
      };

      // Polling fallback
      checkIntervalRef.current = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
          renderRecaptcha();
        }
      }, 100);

      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
          console.error("reCAPTCHA failed to load within 10 seconds");
          if (onError) onError(new Error("reCAPTCHA load timeout"));
        }
      }, 10000);

      // Cleanup
      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
        clearTimeout(timeout);
        if (widgetIdRef.current !== null && window.grecaptcha) {
          try {
            window.grecaptcha.reset(widgetIdRef.current);
          } catch (e) {
            console.error("Error resetting reCAPTCHA:", e);
          }
        }
      };
    }, []);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getValue: () => {
        if (widgetIdRef.current !== null && window.grecaptcha) {
          try {
            return window.grecaptcha.getResponse(widgetIdRef.current);
          } catch (error) {
            console.error("Error getting reCAPTCHA value:", error);
            return null;
          }
        }
        return null;
      },
      reset: () => {
        if (widgetIdRef.current !== null && window.grecaptcha) {
          try {
            window.grecaptcha.reset(widgetIdRef.current);
            if (onChange) onChange(null);
          } catch (error) {
            console.error("Error resetting reCAPTCHA:", error);
          }
        }
      },
    }));

    return (
      <div className={className}>
        <div ref={containerRef}></div>
      </div>
    );
  },
);

ReCaptcha.displayName = "ReCaptcha";

export default ReCaptcha;
